import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Button,
} from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";

const parseComments = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];

    const grouped = parsed.reduce((acc, item) => {
      const name = item?.productName?.trim();
      if (!name) return acc;
      const count = Number(item?.count ?? item?.productCount ?? 0);
      acc[name] = (acc[name] || 0) + count;
      return acc;
    }, {});

    return Object.entries(grouped).map(([name, count]) => `${name} - ${count}`);
  } catch {
    return [];
  }
};

// ─── Comments Cell Renderer ───────────────────────────────────────────────────

const CommentsCellRenderer = ({ value }) => {
  const lines = parseComments(value);
  if (lines.length === 0) {
    return <Text color="gray.400" fontSize="sm">—</Text>;
  }
  return (
    <Box py={1}>
      {lines.map((line, i) => (
        <Text key={i} fontSize="sm" color="gray.700" lineHeight="1.7">
          {line}
        </Text>
      ))}
    </Box>
  );
};

// ─── AG Grid styling ────────────────────────

const gridSx = {
  "& .ag-header": { backgroundColor: "#C72127", borderBottom: "none" },
  "& .ag-header-cell": {
    backgroundColor: "#C72127",
    color: "white",
    fontWeight: "bold",
    borderRight: "1px solid rgba(255,255,255,0.2)",
  },
  "& .ag-header-cell-text": { color: "white", fontSize: "14px", fontWeight: "bold" },
  "& .ag-header-icon": { color: "white" },
  "& .ag-cell": {
    fontSize: "14px",
    padding: "8px 12px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    borderRight: "2px solid #cbd5e0",
    borderBottom: "2px solid #cbd5e0",
  },
  "& .ag-paging-panel": { display: "none" },
  "& .ag-row": { borderBottom: "1px solid #cbd5e0" },
  "& .ag-root-wrapper": { border: "1px solid #cbd5e0" },
  "& .ag-filter": {
    backgroundColor: "white",
    padding: "12px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  "& .ag-filter input": {
    padding: "8px 12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
  },
  "& .ag-filter select": {
    padding: "8px 12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
    backgroundColor: "white",
  },
  "& .ag-filter button": {
    padding: "6px 12px",
    fontSize: "13px",
    borderRadius: "6px",
    marginTop: "8px",
  },
};

// ─── Column definitions ───────────────────────────────────────────────────────

const columnDefs = [
  { headerName: "STORE ID",           field: "storeId",           minWidth: 120, flex: 1 },
  { headerName: "STORE NAME",         field: "storeName",         minWidth: 180, flex: 1.5 },
  { headerName: "STORE TYPE",         field: "storeType",         minWidth: 130, flex: 1 },
  {
    headerName: "AVAILABILITY SCORE",
    field: "availabilityScore",
    minWidth: 160,
    flex: 1,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      color: "#2D3748",
    },
  },
  {
    headerName: "COMMENTS",
    field: "comments",
    minWidth: 280,
    flex: 2.5,
    cellRenderer: CommentsCellRenderer,
    autoHeight: true,
    wrapText: true,
    cellStyle: {
      display: "flex",
      alignItems: "flex-start",
      paddingTop: "8px",
      paddingBottom: "8px",
      whiteSpace: "normal",
      lineHeight: "1.6",
    },
  },
];

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  suppressMovable: true,
  filterParams: { maxNumConditions: 1, buttons: ["reset"] },
  suppressCellFocus: true,
  cellStyle: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "normal",
    lineHeight: "1.5",
    fontSize: "14px",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

function AvailabilityDp({ fromDate, toDate }) {
  const [rowData,       setRowData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [gridApi,       setGridApi]       = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [currentPage,   setCurrentPage]   = useState(1);
  const pageSize = 20;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    setRowData([]);

    const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
    const [toYear,   toMonth,   toDay]   = toDate.split("-").map(Number);

    const requestBody = {
      from: { date: fromDay,  month: fromMonth, year: fromYear },
      to:   { date: toDay,    month: toMonth,   year: toYear   },
    };

    try {
      const res  = await apiClient.post(
        `${API_CONFIG.BASE_URL}/api/availability/stores`,
        requestBody
      );
      const list = Array.isArray(res.data) ? res.data : [res.data];

      setRowData(
        list.map((item) => ({
          storeId:           item?.storeId           ?? "",
          storeName:         item?.storeName         ?? "",
          storeType:         item?.storeType         ?? "",
          availabilityScore: item?.availabilityScore ?? "",
          comments:          item?.comments          ?? "",
        }))
      );
    } catch (err) {
      console.error("Failed to fetch availability data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Grid callbacks ─────────────────────────────────────────────────────────

  const onGridReady = (params) => {
    setGridApi(params.api);
    setFilteredCount(params.api.getDisplayedRowCount());
  };

  const onFilterChanged = useCallback((params) => {
    if (params?.api) setFilteredCount(params.api.getDisplayedRowCount());
  }, []);

  const totalPages = Math.ceil(filteredCount / pageSize);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
      gridApi?.paginationGoToPreviousPage();
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
      gridApi?.paginationGoToNextPage();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Record count pill */}
      <Flex align="center" mb={4}>
        <Flex
          align="center"
          gap={1.5}
          bg="gray.700"
          px={4}
          py={1.5}
          borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.2)"
        >
          <Text fontSize="sm" fontWeight="bold" color="white">
            {filteredCount < rowData.length ? "Filtered:" : "Total:"}
          </Text>
          <Text color="white" fontSize="md" fontWeight="bold">
            {filteredCount} Records
          </Text>
        </Flex>
      </Flex>

      {error && (
        <Text color="red.500" fontWeight="bold" mb={3}>{error}</Text>
      )}

      {/* AG Grid */}
      <Box
        className="ag-theme-alpine"
        h={{ base: "300px", sm: "400px", md: "500px", lg: "600px" }}
        w="100%"
        bg="white"
        borderRadius="8px"
        boxShadow="0 2px 8px rgba(0,0,0,0.1)"
        mb={3}
        overflow="hidden"
        sx={gridSx}
      >
        {loading ? (
          <Flex justify="center" align="center" h="100%">
            <Spinner size="xl" color="red.500" thickness="6px" />
          </Flex>
        ) : (
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination
            paginationPageSize={pageSize}
            domLayout="normal"
            suppressCellFocus
            onFilterChanged={onFilterChanged}
            animateRows
            rowHeight={60}
            headerHeight={65}
            suppressPaginationPanel
            onGridReady={onGridReady}
            onPaginationChanged={(params) =>
              setCurrentPage(params.api.paginationGetCurrentPage() + 1)
            }
            enableCellTextSelection
            ensureDomOrder
            overlayNoRowsTemplate={`
              <div style="padding:40px;text-align:center;">
                <div style="font-size:18px;font-weight:bold;color:#2D3748;margin-bottom:8px;">No Data Available</div>
                <div style="font-size:14px;color:#718096;">No records found for the selected date range</div>
              </div>
            `}
          />
        )}
      </Box>

      {/* Custom Pagination */}
      <Flex justify="space-between" align="center" mt={4} px={2}>
        <Text fontSize="sm" color="gray.600">
          Showing {filteredCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(currentPage * pageSize, filteredCount)} of {filteredCount} records
        </Text>

        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious}
            isDisabled={currentPage === 1}
            size="sm"
            variant="outline"
            colorScheme="gray"
            leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>

          <Flex
            align="center"
            justify="center"
            bg="red.500"
            color="white"
            w="40px"
            h="40px"
            borderRadius="md"
            fontWeight="bold"
            fontSize="md"
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {currentPage}
          </Flex>

          <Button
            onClick={handleNext}
            isDisabled={currentPage >= totalPages}
            size="sm"
            variant="outline"
            colorScheme="gray"
            rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

export default AvailabilityDp;